"use client";

import { useEffect, useRef, useCallback } from "react";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import classNames from "classnames";
import formStyles from "../../../styles/Form.module.css";
import styles from "./InternationalPhone.module.css";

interface InternationalPhoneInputProps {
  autoComplete?: string;
  className?: string;
  disabled?: boolean;
  inError?: boolean;
  initialCountry?: string;
  onBlur?: () => void;
  onChange: (value: string) => void;
  onValidChange?: (isValid: boolean) => void;
  placeholder?: string;
  required?: boolean;
  requiredPlaceholder?: string;
  value?: string;
}

/**
 * International phone input component using intl-tel-input library.
 * Provides country code selection with flag dropdown and phone number validation.
 *
 * This is a controlled input that works with react-hook-form via the Field component.
 */
function InternationalPhoneInput({
  autoComplete = "tel",
  className = "",
  disabled = false,
  inError = false,
  initialCountry = "us",
  onBlur,
  onChange,
  onValidChange,
  placeholder,
  required = false,
  requiredPlaceholder = "",
  value = "",
}: InternationalPhoneInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const itiRef = useRef<ReturnType<typeof intlTelInput> | null>(null);
  const isInitializedRef = useRef(false);

  // Determine placeholder text
  const placeholderText = placeholder ?? (required ? requiredPlaceholder : "");

  // Memoized validation handler
  const handleValidation = useCallback(() => {
    if (!itiRef.current || !inputRef.current) return;

    try {
      const isValid = itiRef.current.isValidNumber();
      const fullNumber = itiRef.current.getNumber();

      onValidChange?.(isValid ?? false);
      onChange(fullNumber || inputRef.current.value);
    } catch {
      onValidChange?.(false);
      onChange(inputRef.current?.value || "");
    }
  }, [onChange, onValidChange]);

  // Initialize intl-tel-input
  useEffect(() => {
    if (!inputRef.current || isInitializedRef.current) return;

    isInitializedRef.current = true;

    itiRef.current = intlTelInput(inputRef.current, {
      initialCountry,
      separateDialCode: true,
      utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@24.6.0/build/js/utils.js",
      containerClass: styles.container,
      dropdownContainer: document.body,
    });

    // Set initial value if provided
    if (value) {
      itiRef.current.setNumber(value);
    }

    // Add event listeners
    const input = inputRef.current;
    input.addEventListener("input", handleValidation);
    input.addEventListener("countrychange", handleValidation);
    input.addEventListener("blur", () => {
      handleValidation();
      onBlur?.();
    });

    return () => {
      input.removeEventListener("input", handleValidation);
      input.removeEventListener("countrychange", handleValidation);
      input.removeEventListener("blur", handleValidation);

      if (itiRef.current) {
        itiRef.current.destroy();
        itiRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, []);

  // Update value when prop changes (for controlled input behavior)
  useEffect(() => {
    if (itiRef.current && value !== undefined) {
      const currentNumber = itiRef.current.getNumber();
      if (currentNumber !== value && value) {
        itiRef.current.setNumber(value);
      }
    }
  }, [value]);

  return (
    <div className={classNames(styles.wrapper, { [styles.disabled]: disabled })}>
      <input
        ref={inputRef}
        type="tel"
        autoComplete={autoComplete}
        disabled={disabled}
        placeholder={placeholderText}
        className={classNames(
          formStyles.input,
          styles.input,
          className,
          "shadow-sm block text-base border-gray-400 rounded-md",
          {
            [formStyles.error]: inError,
            [formStyles.disabled]: disabled,
            "placeholder-red-900/50": inError,
            "placeholder-gray-500/50": !inError,
          }
        )}
        aria-label="Phone number"
        aria-required={required}
        aria-invalid={inError}
      />
    </div>
  );
}

export default InternationalPhoneInput;
